import React from 'react';
import { MinusIcon, PlusIcon, TrashIcon, ShoppingBagIcon, ArrowLeftIcon } from 'lucide-react';
import type { CartItem } from '../App';
interface CartProps {
  items: CartItem[];
  onUpdateQuantity: (id: string, quantity: number) => void;
  onRemoveItem: (id: string) => void;
  onNavigate: (page: string) => void;
}
export function Cart({
  items,
  onUpdateQuantity,
  onRemoveItem,
  onNavigate
}: CartProps) {
  const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const tax = subtotal * 0.06;
  const total = subtotal + tax;
  return <main className="pt-24 min-h-screen bg-matte px-6" data-id="element-144">
      <div className="max-w-4xl mx-auto py-16" data-id="element-145">
        {/* Header */}
        <div className="flex items-center justify-between mb-12 fade-section" data-id="element-146">
          <div data-id="element-147">
            <button onClick={() => {
            onNavigate('menu');
            window.scrollTo(0, 0);
          }} className="flex items-center gap-2 text-white/50 hover:text-gold transition-colors duration-300 font-sans text-sm uppercase tracking-widest mb-4" data-id="element-148">
              <ArrowLeftIcon className="w-4 h-4" data-id="element-149" />
              Back to Menu
            </button>
            <h1 className="font-serif text-[38px] md:text-[50px] text-white" data-id="element-150">
              YOUR ORDER
            </h1>
          </div>
          <ShoppingBagIcon className="w-10 h-10 text-gold/30" data-id="element-151" />
        </div>

        {items.length === 0 ? <div className="text-center py-28 fade-section" data-id="element-152">
            <ShoppingBagIcon className="w-16 h-16 text-white/10 mx-auto mb-6" data-id="element-153" />
            <h2 className="font-serif text-[26px] text-white mb-4" data-id="element-154">
              Your cart is empty
            </h2>
            <p className="font-sans text-lg text-white/50 mb-8" data-id="element-155">
              Explore our menu and add some delicious West African dishes.
            </p>
            <button onClick={() => {
          onNavigate('menu');
          window.scrollTo(0, 0);
        }} className="border border-gold text-gold px-8 py-4 font-sans uppercase tracking-widest text-base font-medium hover:bg-gold/10 transition-all duration-300" data-id="element-156">
              Browse Menu
            </button>
          </div> : <div className="fade-section" data-id="element-157">
            {/* Cart Items */}
            <div className="space-y-6 mb-12" data-id="element-158">
              {items.map(item => <div key={item.id} className="bg-charcoal border border-white/5 flex flex-col sm:flex-row items-stretch" data-id="element-159">
                  {/* Image */}
                  <div className="w-full sm:w-32 h-32 sm:h-auto flex-shrink-0" data-id="element-160">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-161" />
                  </div>

                  {/* Details */}
                  <div className="flex-grow p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-162">
                    <div className="flex-grow" data-id="element-163">
                      <h3 className="font-serif text-xl text-white mb-1" data-id="element-164">
                        {item.name}
                      </h3>
                      <p className="font-sans text-base text-gold" data-id="element-165">
                        ${item.price.toFixed(2)} each
                      </p>
                    </div>

                    {/* Quantity Controls */}
                    <div className="flex items-center gap-4" data-id="element-166">
                      <div className="flex items-center border border-white/10" data-id="element-167">
                        <button onClick={() => onUpdateQuantity(item.id, item.quantity - 1)} className="w-10 h-10 flex items-center justify-center text-white/60 hover:text-gold hover:bg-white/5 transition-colors" aria-label="Decrease quantity" data-id="element-168">
                          <MinusIcon className="w-4 h-4" data-id="element-169" />
                        </button>
                        <span className="w-12 h-10 flex items-center justify-center font-sans text-base text-white border-x border-white/10" data-id="element-170">
                          {item.quantity}
                        </span>
                        <button onClick={() => onUpdateQuantity(item.id, item.quantity + 1)} className="w-10 h-10 flex items-center justify-center text-white/60 hover:text-gold hover:bg-white/5 transition-colors" aria-label="Increase quantity" data-id="element-171">
                          <PlusIcon className="w-4 h-4" data-id="element-172" />
                        </button>
                      </div>

                      <span className="font-sans text-lg text-white w-20 text-right" data-id="element-173">
                        ${(item.price * item.quantity).toFixed(2)}
                      </span>

                      <button onClick={() => onRemoveItem(item.id)} className="text-white/30 hover:text-red-400 transition-colors duration-300 ml-2" aria-label="Remove item" data-id="element-174">
                        <TrashIcon className="w-5 h-5" data-id="element-175" />
                      </button>
                    </div>
                  </div>
                </div>)}
            </div>

            {/* Order Summary */}
            <div className="bg-charcoal border border-white/5 p-8 md:p-10 max-w-md ml-auto" data-id="element-176">
              <h3 className="font-sans text-sm tracking-ultra-wide text-gold uppercase mb-6" data-id="element-177">
                Order Summary
              </h3>
              <div className="space-y-3 mb-6" data-id="element-178">
                <div className="flex justify-between font-sans text-base text-white/70" data-id="element-179">
                  <span data-id="element-180">Subtotal</span>
                  <span data-id="element-181">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-sans text-base text-white/70" data-id="element-182">
                  <span data-id="element-183">Tax (6%)</span>
                  <span data-id="element-184">${tax.toFixed(2)}</span>
                </div>
                <div className="w-full h-px bg-white/10 my-4" data-id="element-185" />
                <div className="flex justify-between font-serif text-xl text-white" data-id="element-186">
                  <span data-id="element-187">Total</span>
                  <span className="text-gold" data-id="element-188">${total.toFixed(2)}</span>
                </div>
              </div>

              <button onClick={() => {
            onNavigate('checkout');
            window.scrollTo(0, 0);
          }} className="w-full bg-gold text-black py-4 font-sans uppercase tracking-widest text-base font-bold hover:bg-gold-light transition-colors duration-300" data-id="element-189">
                Proceed to Checkout
              </button>
            </div>
          </div>}
      </div>
    </main>;
}